/**
 * DEV-ONLY: Media composition helpers for Layout Inspector (cropped/card images).
 * Supports card-owned CSS variables vs inline fallback on <img>.
 */

const TARGETS = [
  {
    id: "mh-program-card",
    label: "CSS variables on .mh-program-card",
    cardSelector: ".mh-program-card",
    mediaSelector: ".mh-program-card__media",
    positionVar: "--program-image-position",
    scaleVar: "--program-image-scale",
  },
  {
    id: "mcp2-review-card",
    label: "CSS variables on .mcp2-review-card",
    cardSelector: ".mcp2-review-card",
    mediaSelector: ".mcp2-review-card__media",
    positionVar: "--review-image-position",
    scaleVar: "--review-image-scale",
    heightVar: "--review-card-height",
  },
];

function getTargetForImage(img) {
  if (!isImageCompositionTarget(img)) return null;
  return TARGETS.find((t) => {
    const media = img.closest(t.mediaSelector);
    const card = img.closest(t.cardSelector);
    return Boolean(media && card && media.contains(img));
  }) || null;
}

function readPrevVarsMap(card) {
  if (!card) return {};
  const raw = card.getAttribute("data-layout-inspector-prev-vars");
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writePrevVarsMap(card, map) {
  if (!card) return;
  const keys = Object.keys(map || {});
  if (keys.length === 0) {
    card.removeAttribute("data-layout-inspector-prev-vars");
    return;
  }
  card.setAttribute("data-layout-inspector-prev-vars", JSON.stringify(map));
}

function rememberVarBeforeOverride(card, varName) {
  if (!card || !card.style || !varName) return;
  const map = readPrevVarsMap(card);
  if (Object.prototype.hasOwnProperty.call(map, varName)) return;
  map[varName] = card.style.getPropertyValue(varName) || "";
  writePrevVarsMap(card, map);
}

function restoreVarAfterOverride(card, varName) {
  if (!card || !card.style || !varName) return;
  const map = readPrevVarsMap(card);
  if (!Object.prototype.hasOwnProperty.call(map, varName)) return;
  const prev = map[varName];
  if (prev) {
    card.style.setProperty(varName, prev);
  } else {
    card.style.removeProperty(varName);
  }
  delete map[varName];
  writePrevVarsMap(card, map);
}

/**
 * @param {Element | null} el
 * @returns {boolean}
 */
export function isImageCompositionTarget(el) {
  return Boolean(el && el.tagName && el.tagName.toLowerCase() === "img");
}

/**
 * @param {HTMLImageElement} img
 * @returns {HTMLElement | null}
 */
export function getProgramCardForMediaImage(img) {
  if (!isImageCompositionTarget(img)) return null;
  const t = getTargetForImage(img);
  if (!t || t.id !== "mh-program-card") return null;
  return img.closest(t.cardSelector);
}

/**
 * @param {HTMLImageElement} img
 * @returns {{ mode: "css-vars" | "inline"; label: string; card: HTMLElement | null }}
 */
export function getMediaCompositionTargetMeta(img) {
  const target = getTargetForImage(img);
  if (target) {
    const card = img.closest(target.cardSelector);
    return {
      mode: "css-vars",
      label: target.label,
      card: card || null,
      targetId: target.id,
    };
  }
  return {
    mode: "inline",
    label: "Inline styles on selected img",
    card: null,
    targetId: null,
  };
}

/**
 * Parse object-position into X/Y percentages (best effort).
 * @param {string} raw
 * @returns {{ x: number; y: number }}
 */
export function parseObjectPositionPercent(raw) {
  if (!raw || typeof raw !== "string") return { x: 50, y: 50 };
  const s = raw.trim().toLowerCase();
  if (s === "center" || s === "") return { x: 50, y: 50 };
  if (s === "left") return { x: 0, y: 50 };
  if (s === "right") return { x: 100, y: 50 };
  if (s === "top") return { x: 50, y: 0 };
  if (s === "bottom") return { x: 50, y: 100 };

  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    const n = parseFloat(parts[0]);
    if (Number.isFinite(n) && parts[0].includes("%")) return { x: n, y: 50 };
    return { x: 50, y: 50 };
  }

  const xPart = parts[0];
  const yPart = parts[1];
  const x = xPart.includes("%") ? parseFloat(xPart) : 50;
  const y = yPart.includes("%") ? parseFloat(yPart) : 50;
  return {
    x: Number.isFinite(x) ? x : 50,
    y: Number.isFinite(y) ? y : 50,
  };
}

/**
 * @param {string} transform
 * @returns {number}
 */
export function parseScaleFromTransform(transform) {
  if (!transform || transform === "none") return 1;
  const m = transform.match(/scale\(\s*([^)]+)\s*\)/);
  if (m) {
    const inner = m[1].trim().split(/[,\s]+/)[0];
    const n = parseFloat(inner);
    return Number.isFinite(n) ? n : 1;
  }
  const n = parseFloat(transform);
  return Number.isFinite(n) ? n : 1;
}

/**
 * Read effective position X/Y (%) and scale from DOM (no overrides).
 * @param {HTMLImageElement} img
 * @param {{ mode: "css-vars" | "inline"; card: HTMLElement | null; targetId?: string | null }} meta
 * @returns {{ positionX: number; positionY: number; scale: number }}
 */
export function readMediaCompositionFromDom(img, meta) {
  if (meta.mode === "css-vars" && meta.card) {
    const target = TARGETS.find((t) => t.id === meta.targetId) || TARGETS[0];
    const positionVar = target?.positionVar || TARGETS[0].positionVar;
    const scaleVar = target?.scaleVar || TARGETS[0].scaleVar;
    const cs = window.getComputedStyle(meta.card);
    const posRaw = cs.getPropertyValue(positionVar).trim() || "50% 50%";
    const scaleRaw = cs.getPropertyValue(scaleVar).trim() || "1";
    const { x, y } = parseObjectPositionPercent(posRaw);
    const scale = parseFloat(scaleRaw);
    return {
      positionX: x,
      positionY: y,
      scale: Number.isFinite(scale) ? scale : 1,
    };
  }

  const cs = window.getComputedStyle(img);
  const pos = parseObjectPositionPercent(cs.objectPosition || "50% 50%");
  const scale = parseScaleFromTransform(cs.transform || "");
  return {
    positionX: pos.x,
    positionY: pos.y,
    scale,
  };
}

/**
 * @param {{ mode?: string; positionX?: number|string; positionY?: number|string; scale?: number|string } | null | undefined} persisted
 * @param {{ positionX: number; positionY: number; scale: number }} fromDom
 * @param {{ mode: "css-vars" | "inline" }} meta
 */
export function mergeMediaCompositionState(persisted, fromDom, meta) {
  if (!persisted || typeof persisted !== "object" || Object.keys(persisted).length === 0) {
    return {
      mode: meta.mode,
      positionX: fromDom.positionX,
      positionY: fromDom.positionY,
      scale: fromDom.scale,
    };
  }
  const px = Number(persisted.positionX);
  const py = Number(persisted.positionY);
  const sc = Number(persisted.scale);
  return {
    mode: persisted.mode || meta.mode,
    positionX: Number.isFinite(px) ? px : fromDom.positionX,
    positionY: Number.isFinite(py) ? py : fromDom.positionY,
    scale: Number.isFinite(sc) ? sc : fromDom.scale,
  };
}

/**
 * Apply media composition temp edits.
 * @param {HTMLImageElement} img
 * @param {{ mode: string; positionX: number; positionY: number; scale: number } | null | undefined} mc
 */
export function applyMediaCompositionOverrides(img, mc) {
  if (!img || !mc || typeof mc !== "object") return;
  const x = Number.isFinite(Number(mc.positionX)) ? Number(mc.positionX) : 50;
  const y = Number.isFinite(Number(mc.positionY)) ? Number(mc.positionY) : 50;
  const scale = Number.isFinite(Number(mc.scale)) ? Number(mc.scale) : 1;
  const posStr = `${x}% ${y}%`;

  if (mc.mode === "css-vars") {
    const meta = getMediaCompositionTargetMeta(img);
    const target = TARGETS.find((t) => t.id === meta.targetId) || TARGETS[0];
    const card = meta.card;
    if (card && target) {
      rememberVarBeforeOverride(card, target.positionVar);
      rememberVarBeforeOverride(card, target.scaleVar);
      card.style.setProperty(target.positionVar, posStr);
      card.style.setProperty(target.scaleVar, String(scale));
    }
    return;
  }

  img.style.objectPosition = posStr;
  img.style.transform = `scale(${scale})`;
}

/**
 * Clear inspector-applied media composition (inline or CSS vars).
 * @param {HTMLImageElement} img
 * @param {{ mode?: string } | null | undefined} mc
 */
export function clearMediaCompositionOverrides(img, mc) {
  if (!isImageCompositionTarget(img)) return;
  const mode = mc?.mode;
  if (mode === "css-vars") {
    const meta = getMediaCompositionTargetMeta(img);
    const target = TARGETS.find((t) => t.id === meta.targetId) || TARGETS[0];
    const card = meta.card;
    if (card && target) {
      restoreVarAfterOverride(card, target.positionVar);
      restoreVarAfterOverride(card, target.scaleVar);
    }
    return;
  }
  if (mode === "inline") {
    img.style.removeProperty("object-position");
    img.style.removeProperty("transform");
  }
}

/**
 * Card box controls (currently used for mcp2 review card height).
 * Read/write vars on the card root, not the nested media/img.
 */
export function getReviewCardForAnyElement(el) {
  if (!el || !el.closest) return null;
  const card = el.closest(".mcp2-review-card");
  return card || null;
}

export function readReviewCardHeightFromDom(card) {
  if (!card) return { heightRaw: "360px", heightPx: 360 };
  const cs = window.getComputedStyle(card);
  const raw = cs.getPropertyValue("--review-card-height").trim() || "360px";
  const px = parseFloat(raw);
  return {
    heightRaw: raw,
    heightPx: Number.isFinite(px) ? px : 360,
  };
}

export function applyReviewCardHeightOverride(selectedEl, heightPx) {
  const card = getReviewCardForAnyElement(selectedEl);
  if (!card) return;
  const n = Number(heightPx);
  if (!Number.isFinite(n) || n <= 0) return;
  rememberVarBeforeOverride(card, "--review-card-height");
  card.style.setProperty("--review-card-height", `${n}px`);
}

export function clearReviewCardHeightOverride(selectedEl) {
  const card = getReviewCardForAnyElement(selectedEl);
  if (!card) return;
  restoreVarAfterOverride(card, "--review-card-height");
}
